import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { AlertPopup } from '../common/AlertPopup';
import StreetAutocompleteInput from '../common/StreetAutocompleteInput';
import { fetchPicklists } from '../common/constants';
import { AddAccountModal } from '../AccountManagement';
import { formatPhoneNumber } from './phoneUtils';
import { US_STATES } from './contactConstants';
import { 
  toUSStateCode, 
  displayCountry, 
  validateEmail, 
  buildComparableFromSelected 
} from './contactUtils';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import './AddContactModal.css';

export function AddContactModal({ isOpen, onClose, onContactAdded, contact }) {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [accountSearch, setAccountSearch] = useState('');
  const [isEmailDropdownVisible, setIsEmailDropdownVisible] = useState(false);
  const [isAccountDropdownVisible, setIsAccountDropdownVisible] = useState(false);
  const [emailContacts, setEmailContacts] = useState([]);
  const [accountContacts, setAccountContacts] = useState([]);
  const [noEmailFound, setNoEmailFound] = useState(false);
  const [selectedContact, setSelectedContact] = useState({
    Email: '',
    Email_Alternate__c: '',
    Personal_Email__c: '',
    FirstName: '',
    LastName: '',
    Title: '',
    Department: '',
    Type: '',
    GMB_Type__c: '',
    GMB_Region__c: '',
    Phone: '',
    MobilePhone: '',
    Fax: '',
    MailingAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    },
    OtherAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    },
    GMB_Account_Billing_State_Short_Code__c: '',
    AccountName: '',
    AccountId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', isError: false });
  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    isChecking: false,
    exists: false
  });
  const [searchResults, setSearchResults] = useState({
    contacts: [],
    accounts: [],
    opportunities: []
  });
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [noEmail, setNoEmail] = useState(false);
  const [noAccountFound, setNoAccountFound] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [showWrongAccountNote, setShowWrongAccountNote] = useState(false);
  const [showAddAccountButton, setShowAddAccountButton] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [mode, setMode] = useState('add'); // 'add' or 'edit'
  const [isAccountSearchLoading, setIsAccountSearchLoading] = useState(false);
  const [isContactSearchLoading, setIsContactSearchLoading] = useState(false);

  // Debounce state for search functions
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [accountSearchTimeout, setAccountSearchTimeout] = useState(null);

  // Ref for search dropdown click-outside handling
  const searchDropdownRef = useRef(null);

  const [typeOptions, setTypeOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);

  // Track original values of an existing contact for change detection
  const originalComparableRef = useRef(null);

  useEffect(() => {
    fetchPicklists()
      .then(({ typeOptions, regionOptions }) => {
        setTypeOptions(typeOptions);
        setRegionOptions(regionOptions);
      })
      .catch(err => {
        console.error("Failed to load picklists:", err);
      });
  }, []);


  // Handle clicks outside the search dropdown
  useEffect(() => {
    if (!showSearchDropdown) return;

    function handleClickOutside(event) {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchDropdown]);


  // Debounced account search function
  const performAccountSearch = useCallback(async (accountName) => {
    // Check if the original input has at least 3 characters (including spaces)
    if (accountName.length < 3) {
      setIsAccountDropdownVisible(false);
      setAccountContacts([]);
      setNoAccountFound(false);
      setIsAccountSearchLoading(false);
      return;
    }

    // If the input consists only of spaces, don't trigger the API
    if (accountName.trim().length === 0) {
      setIsAccountDropdownVisible(false);
      setAccountContacts([]);
      setNoAccountFound(false);
      setIsAccountSearchLoading(false);
      return;
    }

    setIsAccountSearchLoading(true);
    try {
      if (!isReady) return;

      // Manually encode the search parameter to ensure spaces are encoded as %20
      const encodedSearch = encodeURIComponent(accountName);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/accounts/name/${encodedSearch}`,
        await getAuthConfig()
      );
      const records = response.data.records;

      if (records.message === "No record found for the given account name") {
        setAccountContacts([]);
        setIsAccountDropdownVisible(false);
        setNoAccountFound(true);
        setShowAddAccountButton(false);
        setShowWrongAccountNote(false);
      } else if (records.Id) {
        // Single record found - wrap in array for consistency
        setAccountContacts([records]);
        setIsAccountDropdownVisible(true);
        setNoAccountFound(false);
        setShowWrongAccountNote(false);
        setShowAddAccountButton(false);
      } else if (Array.isArray(records.records)) {
        // Multiple records found - filter to only show accounts that start with the search term
        const searchTerm = accountName.trim().toLowerCase();
        const filteredRecords = records.records.filter(account =>
          account.Name && account.Name.toLowerCase().startsWith(searchTerm)
        );
        setAccountContacts(filteredRecords);
        setIsAccountDropdownVisible(filteredRecords.length > 0);
        setNoAccountFound(filteredRecords.length === 0);
        setShowWrongAccountNote(false);
        setShowAddAccountButton(filteredRecords.length === 0);
      } else {
        // No records found
        setAccountContacts([]);
        setIsAccountDropdownVisible(false);
        setNoAccountFound(true);
        setShowAddAccountButton(false);
        setShowWrongAccountNote(false);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setNoAccountFound(false);
      setShowWrongAccountNote(false);
      setShowAddAccountButton(false);
    } finally {
      setIsAccountSearchLoading(false);
    }
  }, [getAuthConfig, isReady]);

  const handleAccountSearch = (e) => {
    const accountName = e.target.value;
    setSelectedContact((prev) => ({ ...prev, AccountName: accountName, AccountId: '', }));
    setShowWrongAccountNote(false);
    setShowAddAccountButton(false);

    // Clear existing timeout
    if (accountSearchTimeout) {
      clearTimeout(accountSearchTimeout);
    }

    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      performAccountSearch(accountName);
    }, 1000);

    setAccountSearchTimeout(newTimeout);
  };

  const handleAccountSelect = (account) => {
    setSelectedContact((prev) => {
      // Only auto-fill if we're creating a new contact (mode === 'add')
      // Don't auto-fill if we're editing an existing contact (mode === 'edit')
      const isCreatingNewContact = mode === 'add';
      return {
        ...prev,
        AccountName: account.Name,
        AccountId: account.Id,
        // Auto-fill Type and Region from account data only when creating new contact
        Type: isCreatingNewContact ? (account.Type || prev.Type) : prev.Type,
        GMB_Region__c: isCreatingNewContact ? (account.GMB_Region__c || prev.GMB_Region__c) : prev.GMB_Region__c,
        // Auto-fill mailing address from billing address only when creating new contact
        MailingAddress: isCreatingNewContact ? {
          ...prev.MailingAddress,
          street: account.BillingStreet || prev.MailingAddress.street,
          city: account.BillingCity || prev.MailingAddress.city,
          state: account.BillingState || prev.MailingAddress.state,
          postalCode: account.BillingPostalCode || prev.MailingAddress.postalCode,
          country: account.BillingCountry || prev.MailingAddress.country,
        } : prev.MailingAddress,
      };
    });
    setIsAccountDropdownVisible(false);
  };

  const handleGlobalSearch = async (e) => {
    const searchTerm = e.target.value;
    setAccountSearch(searchTerm);

    if (searchTerm.trim().length < 3) {
      setShowSearchDropdown(false);
      setSearchResults({ contacts: [], accounts: [], opportunities: [] });
      return;
    }

    try {
      if (!isReady) return;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/global-search/${searchTerm}`,
        await getAuthConfig()
      );

      if (response.data && response.data.records) {
        const { contacts, accounts, opportunities } = response.data.records;
        setSearchResults({
          contacts: contacts || [],
          accounts: accounts || [],
          opportunities: opportunities || []
        });
        setShowSearchDropdown(true);
      } else {
        setSearchResults({ contacts: [], accounts: [], opportunities: [] });
        setShowSearchDropdown(false);
      }
    } catch (error) {
      console.error("Error in global search:", error);
      setSearchResults({ contacts: [], accounts: [], opportunities: [] });
      setShowSearchDropdown(false);
    }
  };

  const handleSearchResultSelect = (item, type) => {
    if (type === 'contact') {
      handleContactSelect(item);
    } else if (type === 'account') {
      handleAccountSelect(item);
    }
    setShowSearchDropdown(false);
  };

  const handleEmailSearch = async (e) => {
    const searchTerm = e.target.value.trim();
    setAccountSearch(searchTerm);

    if (searchTerm.length < 3) {
      setNoEmailFound(false);
      setEmailContacts([]);
      setIsEmailDropdownVisible(false);
      return;
    }

    if (searchTerm === selectedContact.Email) {
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
    }

    setSelectedContact((prev) => ({
      ...prev,
      Email: "",
      Id: prev.Id || "",
      Email_Alternate__c: "",
      Personal_Email__c: "",
      FirstName: "",
      LastName: "",
      Title: "",
      Department: "",
      Type: "",
      GMB_Type__c: "",
      GMB_Region__c: "",
      Phone: "",
      MobilePhone: "",
      Fax: "",
      MailingAddress: {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
      },
      OtherAddress: {
        street: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
      },
      GMB_Account_Billing_State_Short_Code__c: "",
      AccountName: "",
      AccountId: prev.AccountId || "",
    }));

    try {
      if (!isReady) return;
      // Search by email, name, or phone
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/search/${searchTerm}`,
        await getAuthConfig()
      );
      const data = response.data;

      if (data.records?.message === "No record found" ||
        data.message === "No record found") {
        setNoEmailFound(true);
        setEmailContacts([]);
        setIsEmailDropdownVisible(false);
      } else if (data.records?.records && Array.isArray(data.records.records) && data.records.records.length > 0) {
        setNoEmailFound(false);
        setEmailContacts(data.records.records);
        setIsEmailDropdownVisible(true);
      } else {
        setNoEmailFound(true);
        setEmailContacts([]);
        setIsEmailDropdownVisible(false);
      }
    } catch (error) {
      console.error("Error searching contacts:", error);
      setNoEmailFound(true);
      setEmailContacts([]);
      setIsEmailDropdownVisible(false);
    }
  };

  const handleContactSelect = (contact) => {
    console.log('Selected contact:', contact);

    // Parse the Name field into FirstName and LastName
    const nameParts = contact.Name ? contact.Name.split(' ') : ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    setSelectedContact({
      ...selectedContact,
      Id: contact.Id || contact.id || '',
      id: contact.id || '',
      Email: contact.Email || '',
      Email_Alternate__c: contact.Email_Alternate__c || '',
      Personal_Email__c: contact.Personal_Email__c || '',
      FirstName: firstName,
      LastName: lastName,
      Title: contact.Title || '',
      Department: contact.Department || '',
      Type: contact.GMB_Type__c || '',
      GMB_Type__c: contact.GMB_Type__c || '',
      GMB_Region__c: contact.GMB_Region__c || '',
      Phone: contact.Phone || '',
      MobilePhone: contact.MobilePhone || '',
      Fax: contact.Fax || '',
      MailingAddress: {
        street: contact.MailingStreet || '',
        city: contact.MailingCity || '',
        state: contact.MailingState || '',
        postalCode: contact.MailingPostalCode || '',
        country: contact.MailingCountry || '',
      },
      OtherAddress: {
        street: contact.OtherStreet || '',
        city: contact.OtherCity || '',
        state: contact.OtherState || '',
        postalCode: contact.OtherPostalCode || '',
        country: contact.OtherCountry || '',
      },
      GMB_Account_Billing_State_Short_Code__c: contact.GMB_Account_Billing_State_Short_Code__c || '',
      AccountName: contact.Account?.Name || '',
      AccountId: contact.AccountId || '',
      contactType: contact.contactType || '',
    });
    setAccountSearch(contact.Email || ''); // Sync selected email to input
    setShowSearchDropdown(false); // Close the dropdown
    setFilteredContacts([]); // Clear filtered results
    setNoEmail(!contact.Email); // <-- Check the 'No email exists' box if no email
    setMode('edit');
  };

  const validateEmailField = async (email) => {
    if (email === accountSearch) {
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
      return;
    }

    if (!email || !validateEmail(email)) {
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
      return;
    }

    setEmailValidation(prev => ({ ...prev, isChecking: true }));
    try {
      if (!isReady) return;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/email/${email}`,
        await getAuthConfig()
      );

      // Debug logging to see the API response
      console.log('Email validation API response:', response.data);

      // Handle different API response structures
      let exists = false;

      if (response.data.total !== undefined) {
        // New format with total field
        exists = response.data.total > 0;
        console.log('Using total field - exists:', exists, 'total:', response.data.total);
      } else if (response.data.records) {
        // Old format - check if records exist and have content
        if (response.data.records.message === "No record found for the given email") {
          exists = false;
        } else if (Array.isArray(response.data.records.records)) {
          exists = response.data.records.records.length > 0;
        } else if (response.data.records.Id) {
          // Single record found
          exists = true;
        }
        console.log('Using records format - exists:', exists);
      }

      console.log('Final email validation - exists:', exists);

      // Set validation state - email is valid if it doesn't exist
      const isValid = !exists;
      setEmailValidation({
        isValid: isValid,
        isChecking: false,
        exists: exists
      });

      console.log('Email validation state set to:', {
        isValid: isValid,
        isChecking: false,
        exists: exists
      });
    } catch (error) {
      console.error("Error checking email:", error);
      setEmailValidation({ isValid: false, isChecking: false, exists: false });
    }
  };

  // Helper to check required fields
  const getFormErrors = (fields) => {
    const errors = {};
    if (!fields.AccountId) errors.AccountId = 'Account is required';
    if (!fields.FirstName) errors.FirstName = 'First Name is required';
    if (!fields.LastName) errors.LastName = 'Last Name is required';
    if (!fields.Type) errors.Type = 'Type is required';
    if (!fields.GMB_Region__c) errors.GMB_Region__c = 'Region is required';
    if (!fields.Email && !noEmail) errors.Email = 'Email is required or check No email exists';
    return errors;
  };

  const handleCreateContact = async () => {
    const errors = getFormErrors(selectedContact);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setAlert({
        show: true,
        message: 'Please fill all required fields: Account, First Name, Last Name, Email (or check No email exists), Type, and Region',
        isError: true
      });
      return;
    }

    const {
      FirstName,
      LastName,
      AccountId,
      Title,
      Department,
      Phone,
      MobilePhone,
      Fax,
      Email,
      Email_Alternate__c,
      Personal_Email__c,
      Type,
      GMB_Region__c,
      MailingAddress,
      OtherAddress,
      GMB_Account_Billing_State_Short_Code__c,
    } = selectedContact;

    // Debug logging to see what name fields are being sent
    console.log('Contact creation payload - name fields:', {
      FirstName,
      LastName
    });

    // Prepare payload for Salesforce API
    const salesforcePayload = {
      Salutation: "Mr.",
      FirstName,
      LastName,
      AccountId,
      Title: Title || "",
      Department: Department || "",
      Phone: Phone || "",
      MobilePhone: MobilePhone || "",
      Fax: Fax || null,
      Email: Email || "",
      No_Email__c: !Email || noEmail ? true : false,
      Email_Alternate__c: Email_Alternate__c || "",
      Personal_Email__c: Personal_Email__c || "",
      GMB_Type__c: Type,
      GMB_Region__c,
      MailingStreet: MailingAddress?.street || "",
      MailingCity: MailingAddress?.city || "",
      MailingState: MailingAddress?.state || "",
      MailingPostalCode: MailingAddress?.postalCode || "",
      MailingCountry: MailingAddress?.country || "",
      GMB_Account_Billing_State_Short_Code__c: GMB_Account_Billing_State_Short_Code__c || "",
      OtherStreet: OtherAddress?.street || "",
      OtherCity: OtherAddress?.city || "",
      OtherState: OtherAddress?.state || "",
      OtherPostalCode: OtherAddress?.postalCode || "",
      OtherCountry: OtherAddress?.country || "",
      Project_Site_Contact_Last_Modified_By__c: (window.__currentUserDisplayName || ''),
      Project_Site_Contact_Last_Modified_Date__c: new Date().toISOString()
    };

    setIsLoading(true);
    try {
      if (!isReady) return;
      // Create contact in Salesforce
      await axios.post(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts`,
        salesforcePayload,
        await getAuthConfig({ headers: { "Content-Type": "application/json" } })
      );

      // Fetch updated contacts list
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/all-contacts`,
        await getAuthConfig()
      );

      // Update parent component with new contacts
      if (onContactAdded) {
        onContactAdded(response.data.records);
      }

      setIsSuccess('created');
      setIsLoading(false);

      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (error) {
      setIsLoading(false);
      if (error.response) {
        console.error("Error creating contact:", error.response.data);
        setAlert({
          show: true,
          message: `Failed to create contact! ${error.response.data.message || "Unknown error"}`,
          isError: true
        });
      } else {
        console.error("Error creating contact:", error);
        setAlert({
          show: true,
          message: "Failed to create contact due to a network error or invalid response.",
          isError: true
        });
      }
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault && e.preventDefault();
    const errors = getFormErrors(selectedContact);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setAlert({
        show: true,
        message: 'Please fill all required fields: Account, First Name, Last Name, Email (or check No email exists), Type, and Region',
        isError: true
      });
      return;
    }

    const {
      FirstName,
      LastName,
      AccountId,
      Title,
      Department,
      Phone,
      MobilePhone,
      Fax,
      Email,
      Email_Alternate__c,
      Personal_Email__c,
      Type,
      GMB_Region__c,
      MailingAddress,
      OtherAddress,
      GMB_Account_Billing_State_Short_Code__c,
      Id
    } = selectedContact;

    // Prepare payload for Salesforce API
    const salesforcePayload = {
      Salutation: "Mr.",
      FirstName,
      LastName,
      AccountId,
      Title: Title || "",
      Department: Department || "",
      Phone: Phone || "",
      MobilePhone: MobilePhone || "",
      Fax: Fax || null,
      Email: Email || "",
      No_Email__c: !Email || noEmail ? true : false,
      Email_Alternate__c: Email_Alternate__c || "",
      Personal_Email__c: Personal_Email__c || "",
      GMB_Type__c: Type,
      GMB_Region__c,
      MailingStreet: MailingAddress?.street || '',
      MailingCity: MailingAddress?.city || '',
      MailingState: MailingAddress?.state || '',
      MailingPostalCode: MailingAddress?.postalCode || '',
      MailingCountry: MailingAddress?.country || '',
      GMB_Account_Billing_State_Short_Code__c: GMB_Account_Billing_State_Short_Code__c || '',
      OtherStreet: OtherAddress?.street || '',
      OtherCity: OtherAddress?.city || '',
      OtherState: OtherAddress?.state || '',
      OtherPostalCode: OtherAddress?.postalCode || '',
      OtherCountry: OtherAddress?.country || ''
    };

    setIsLoading(true);
    try {
      if (!isReady) return;
      // Update contact in Salesforce
      // Conditionally add last-modified fields only if meaningful changes occurred
      const currentComparable = buildComparableFromSelected(selectedContact);
      const originalComparable = originalComparableRef.current;
      const hasChanges = !originalComparable || JSON.stringify(currentComparable) !== JSON.stringify(originalComparable);
      const payloadToSend = hasChanges ? {
        ...salesforcePayload,
        Project_Site_Contact_Last_Modified_By__c: (window.__currentUserDisplayName || ''),
        Project_Site_Contact_Last_Modified_Date__c: new Date().toISOString()
      } : salesforcePayload;

      await axios.patch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/id/${Id}`,
        payloadToSend,
        await getAuthConfig({ headers: { "Content-Type": "application/json" } })
      );

      // Fetch updated contacts list
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/all-contacts`,
        await getAuthConfig()
      );

      // Update parent component with new contacts
      if (onContactAdded) {
        onContactAdded(response.data.records);
      }

      setIsSuccess('updated');
      setIsLoading(false);

      // Close modal after success
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (error) {
      setIsLoading(false);
      if (error.response) {
        console.error("Error updating contact:", error.response.data);
        setAlert({
          show: true,
          message: `Failed to update contact! ${error.response.data.message || "Unknown error"}`,
          isError: true
        });
      } else {
        console.error("Error updating contact:", error);
        setAlert({
          show: true,
          message: "Failed to update contact due to a network error or invalid response.",
          isError: true
        });
      }
    }
  };

  const handleCloseAlert = () => {
    setAlert({ show: false, message: '', isError: false });
  };

  useEffect(() => {
    if (isOpen && contact) {
      setMode('edit');
      // When contact data is passed, populate the form
      const nextSelected = {
        Email: contact.Email || '',
        Email_Alternate__c: contact.Email_Alternate__c || '',
        Personal_Email__c: contact.Personal_Email__c || '',
        FirstName: contact.FirstName || '',
        LastName: contact.LastName || '',
        Title: contact.Title || '',
        Department: contact.Department || '',
        Type: contact.GMB_Type__c || '',
        GMB_Type__c: contact.GMB_Type__c || '',
        GMB_Region__c: contact.GMB_Region__c || '',
        Phone: contact.Phone || '',
        MobilePhone: contact.MobilePhone || '',
        Fax: contact.Fax || '',
        MailingAddress: {
          street: contact.MailingAddress?.street || '',
          city: contact.MailingAddress?.city || '',
          state: contact.MailingAddress?.state || '',
          postalCode: contact.MailingAddress?.postalCode || '',
          country: contact.MailingAddress?.country || '',
        },
        OtherAddress: {
          street: contact.OtherAddress?.street || '',
          city: contact.OtherAddress?.city || '',
          state: contact.OtherAddress?.state || '',
          postalCode: contact.OtherAddress?.postalCode || '',
          country: contact.OtherAddress?.country || '',
        },
        GMB_Account_Billing_State_Short_Code__c: contact.GMB_Account_Billing_State_Short_Code__c || '',
        AccountName: contact.Account?.Name || contact.AccountName || '',
        AccountId: contact.AccountId || '',
        Id: contact.Id || contact.id || '',
        id: contact.id || '',
      };
      setSelectedContact(nextSelected);
      // Capture original snapshot for diff
      setTimeout(() => {
        originalComparableRef.current = buildComparableFromSelected(nextSelected);
      }, 0);
      setAccountSearch(contact.Email || '');
      setNoEmail(contact.Email ? false : true);
      setNoAccountFound(false);
      setShowAddAccountButton(false);
      setShowWrongAccountNote(false);
    } else if (isOpen && !contact) {
      setMode('add');
      // If adding a new contact, reset all fields to blank/default
      setSelectedContact({
        Email: '',
        Email_Alternate__c: '',
        Personal_Email__c: '',
        FirstName: '',
        LastName: '',
        Title: '',
        Department: '',
        Type: '',
        GMB_Type__c: '',
        GMB_Region__c: '',
        Phone: '',
        MobilePhone: '',
        Fax: '',
        MailingAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
        OtherAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
        GMB_Account_Billing_State_Short_Code__c: '',
        AccountName: '',
        AccountId: '',
        Id: '',
        id: '',
      });
      setNoEmail(false);
      setNoAccountFound(false);
      setShowAddAccountButton(false);
      setShowWrongAccountNote(false);
    } else if (!isOpen) {
      // Clear timeouts when modal closes
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      if (accountSearchTimeout) {
        clearTimeout(accountSearchTimeout);
        setAccountSearchTimeout(null);
      }

      // Reset form and search state when modal is closed
      setAccountSearch('');
      setAccountContacts([]);
      setEmailContacts([]);
      setNoEmailFound(false);
      setIsSuccess(false);
      setEmailValidation({
        isValid: false,
        isChecking: false,
        exists: false
      });
      setShowSearchDropdown(false);
      setFilteredContacts([]);
      setSelectedContact({
        Email: '',
        Email_Alternate__c: '',
        Personal_Email__c: '',
        FirstName: '',
        LastName: '',
        Title: '',
        Department: '',
        Type: '',
        GMB_Type__c: '',
        GMB_Region__c: '',
        Phone: '',
        MobilePhone: '',
        Fax: '',
        MailingAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
        OtherAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
        GMB_Account_Billing_State_Short_Code__c: '',
        AccountName: '',
        AccountId: '',
        Id: '',
        id: '',
      });
      setNoEmail(false);
      setNoAccountFound(false);
      setShowAddAccountButton(false);
      setShowWrongAccountNote(false);
      setIsAccountDropdownVisible(false); // Reset account dropdown visibility
      setFormErrors({}); // Reset validation errors on close
      setIsAccountSearchLoading(false); // Reset account search loading state
    }
  }, [isOpen, contact]);

  // Remove the old fetchContacts - we'll only search when user types

  // Debounced search function using new Salesforce search API
  const performSearch = useCallback(async (searchTerm) => {
    if (searchTerm.length < 3) {
      setFilteredContacts([]);
      setShowSearchDropdown(false);
      setMode('add');
      return;
    }

    setIsContactSearchLoading(true);
    try {
      if (!isReady) return;
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/search/${encodeURIComponent(searchTerm)}`,
        await getAuthConfig()
      );
      const data = response.data;
      
      if (data.records && data.records.length > 0) {
        setFilteredContacts(data.records);
        setShowSearchDropdown(true);
      } else {
        setFilteredContacts([]);
        setShowSearchDropdown(true);
      }
    } catch (error) {
      console.error('Error searching contacts:', error);
      setFilteredContacts([]);
      setShowSearchDropdown(false);
    } finally {
      setIsContactSearchLoading(false);
    }
  }, [getAuthConfig, isReady]);

  const handleSearch = (e) => {
    // Don't allow input changes while API call is in progress
    if (isContactSearchLoading) {
      return;
    }

    const searchTerm = e.target.value;
    setAccountSearch(searchTerm);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search (1 second)
    const newTimeout = setTimeout(() => {
      if (searchTerm.length >= 3) {
        performSearch(searchTerm);
      } else if (searchTerm.length > 0) {
        setFilteredContacts([]);
        setShowSearchDropdown(false);
      }
    }, 1000);

    setSearchTimeout(newTimeout);
  };

  const handleEmailCheckbox = (checked) => {
    setNoEmail(checked);
    if (checked) {
      setSelectedContact(prev => ({
        ...prev,
        Email: '',
        Email_Alternate__c: '',
        Personal_Email__c: ''
      }));
    }
  };


  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsSuccess(false);
    onClose();
  };

  return createPortal(
    <div className="add-contact-modal-overlay">
      {alert.show && (
        <AlertPopup
          message={alert.message}
          onClose={handleCloseAlert}
          isError={alert.isError}
        />
      )}
      <div className="add-contact-modal-sheet">
        <div className="add-contact-modal-header">
          <h2 className="add-contact-modal-title">Add New Contact</h2>
          <button
            type="button"
            onClick={handleClose}
            className="add-contact-modal-close"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
            </svg>
          </button>
        </div>

        <div className={`add-contact-modal-body${isSuccess ? ' add-contact-modal-body--success' : ''}`}>
          {isSuccess ? (
            <>
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2 text-center">
                {isSuccess === 'created' ? 'Contact Created Successfully!' : 'Contact Updated Successfully!'}
              </h2>
            </>
          ) : (
            <div className="max-md:p-0">
              <form className="space-y-4 relative" autoComplete="off" autoCorrect="off" spellCheck="false">
                {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm text-gray-600 mb-1">Search Contacts</label>
                  <div ref={searchDropdownRef} className="relative">
                    <div className="flex items-center border rounded-md">
                      <input
                        type="text"
                        value={accountSearch}
                        onChange={handleSearch}
                        placeholder="Search by name, email, or phone (minimum 3 characters)"
                        className={`w-full p-2 border-0 focus:outline-none rounded-md ${formErrors.FirstName || formErrors.LastName ? 'border-red-500' : ''} ${isContactSearchLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        disabled={isLoading || isContactSearchLoading}
                        autoComplete="new-password"
                        autoCorrect="off"
                        spellCheck="false"
                        data-form-type="other"
                      />
                      {isContactSearchLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                      ) : (
                        <img src={Icon13} alt="Search Icon" className="w-5 h-5 mr-2 cursor-pointer" />
                      )}
                    </div>
                    {accountSearch && accountSearch.length > 0 && accountSearch.length < 3 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Type at least 3 characters to search ({accountSearch.length}/3)
                      </div>
                    )}

                    {showSearchDropdown && filteredContacts.length > 0 && (
                      <div className="absolute top-full left-0 bg-white border rounded-md mt-1 w-full shadow-lg z-10 max-h-[400px] overflow-y-auto add-contact-search-dropdown">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile Phone</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredContacts.map((contact) => (
                              <tr
                                key={contact.Id}
                                className="hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleContactSelect(contact)}
                              >
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{contact.Name}</div>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">{contact.Account?.Name || '-'}</div>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">{contact.Email}</div>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">{contact.MobilePhone}</div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {showSearchDropdown && filteredContacts.length === 0 && accountSearch.length >= 3 && (
                      <div className="text-sm text-green-600 mt-1">
                        No matching contacts found. You can create a new one.
                      </div>
                    )}
                  </div>
                </div>

                <div className="add-contact-form-grid add-contact-form-grid-3 add-contact-email-grid">
                  {/* Primary Email */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Email<span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="flex items-center gap-4">
                        <input
                          type="email"
                          value={selectedContact.Email}
                          className={`w-full p-2 border rounded-md ${formErrors.Email ? 'border-red-500' :
                              emailValidation.exists ? 'border-red-500' :
                                emailValidation.isValid ? 'border-green-500' : ''
                            }`}
                          onChange={(e) => {
                            setSelectedContact((prev) => ({ ...prev, Email: e.target.value }));
                            setEmailValidation({ isValid: false, isChecking: false, exists: false });
                          }}
                          onBlur={(e) => validateEmailField(e.target.value)}
                          disabled={isLoading || noEmail}
                          placeholder="Primary Email"
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                          required={!noEmail}
                        />
                        <label className="flex items-center gap-2 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={noEmail}
                            onChange={(e) => {
                              setNoEmail(e.target.checked);
                              if (e.target.checked) {
                                setSelectedContact(prev => ({ ...prev, Email: '' }));
                              }
                            }}
                            className="rounded border-gray-300"
                            disabled={isLoading}
                          />
                          <span className="text-sm text-gray-600">No email exists</span>
                        </label>
                      </div>

                      {emailValidation.isChecking && (
                        <div className="text-xs text-blue-600 mt-1 flex items-center">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                          Checking email...
                        </div>
                      )}
                      {!emailValidation.isChecking && emailValidation.isValid && (
                        <div className="text-xs text-green-600 mt-1">✓ Email is available</div>
                      )}
                      {!emailValidation.isChecking && emailValidation.exists && (
                        <div className="text-xs text-red-600 mt-1">This email already exists</div>
                      )}
                      {formErrors.Email && <div className="text-xs text-red-600 mt-1">{formErrors.Email}</div>}
                    </div>
                  </div>

                  {/* Alternate Email */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Email - Alternate</label>
                    <input
                      type="email"
                      value={selectedContact.Email_Alternate__c}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, Email_Alternate__c: e.target.value }))
                      }
                      disabled={isLoading}
                      placeholder="Alternate Email"
                    />
                  </div>

                  {/* Personal Email */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Personal Email</label>
                    <input
                      type="email"
                      value={selectedContact.Personal_Email__c}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, Personal_Email__c: e.target.value }))
                      }
                      disabled={isLoading}
                      placeholder="Personal Email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Search Account<span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="flex items-center relative">
                        <input
                          type="text"
                          value={selectedContact.AccountName}
                          onChange={handleAccountSearch}
                          placeholder="Search by account name"
                          className="w-full p-2 border rounded-md focus:outline-none"
                          disabled={isLoading || isAccountSearchLoading}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                          data-form-type="other"
                        />
                        {isAccountSearchLoading && (
                          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-md">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-700"></div>
                          </div>
                        )}
                        <img src={Icon13} alt="Search Icon" className="absolute right-2 w-5 h-5 cursor-pointer" />
                      </div>
                      {isAccountDropdownVisible && (
                        <div className="absolute top-full left-0 bg-white border rounded-md mt-1 w-full shadow-lg z-10 max-h-[400px] overflow-y-auto add-contact-search-dropdown">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Name</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {accountContacts.map((account) => (
                                <tr
                                  key={account.Id}
                                  className="hover:bg-gray-100 cursor-pointer"
                                  onClick={() => handleAccountSelect(account)}
                                >
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{account.Name}</div>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">{formatPhoneNumber(account.Phone)}</div>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">
                                      {account.BillingStreet ? (
                                        <>
                                          {account.BillingStreet}
                                          {account.BillingCity && <>, {account.BillingCity}</>}
                                          {account.BillingState && <>, {account.BillingState}</>}
                                          {account.BillingPostalCode && <> {account.BillingPostalCode}</>}
                                        </>
                                      ) : '-'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">
                                      {account.Website ? (
                                        <a
                                          href={account.Website.startsWith('http') ? account.Website : `http://${account.Website}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {account.Website}
                                        </a>
                                      ) : '-'}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    {formErrors.AccountId && <div className="text-xs text-red-600 mt-1">{formErrors.AccountId}</div>}
                    {noAccountFound && !showAddAccountButton && !showWrongAccountNote && (
                      <div className="flex items-center mt-2 text-green-700 gap-2">
                        <span>No account found. Do you want to add account?</span>
                        <button
                          type="button"
                          className="px-2 py-0.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                          onClick={() => { setShowAddAccountButton(true); setShowWrongAccountNote(false); }}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className="px-2 py-0.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-xs"
                          onClick={() => { setShowWrongAccountNote(true); setShowAddAccountButton(false); setNoAccountFound(false); }}
                        >
                          No
                        </button>
                      </div>
                    )}
                    {noAccountFound && showAddAccountButton && !showWrongAccountNote && (
                      <div className="flex items-center mt-2 text-green-700 gap-2">
                        <span>No account found. Do you want to add account?</span>
                        <button
                          type="button"
                          className="text-red-600 text-sm hover:underline whitespace-nowrap"
                          onClick={() => setShowAddAccountModal(true)}
                        >
                          + Add Account
                        </button>
                      </div>
                    )}
                    {showWrongAccountNote && (
                      <div className="flex items-center mt-2 text-red-600 text-sm">Please type right account name.</div>
                    )}
                  </div>
                </div>

                <div className="add-contact-form-grid add-contact-form-grid-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">First Name<span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={selectedContact.FirstName}
                      className={`w-full p-2 border rounded-md ${formErrors.FirstName ? 'border-red-500' : ''}`}
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, FirstName: e.target.value }))
                      }
                      disabled={isLoading}
                    />
                    {formErrors.FirstName && <div className="text-xs text-red-600 mt-1">{formErrors.FirstName}</div>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Last Name<span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={selectedContact.LastName}
                      className={`w-full p-2 border rounded-md ${formErrors.LastName ? 'border-red-500' : ''}`}
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, LastName: e.target.value }))
                      }
                      disabled={isLoading}
                    />
                    {formErrors.LastName && <div className="text-xs text-red-600 mt-1">{formErrors.LastName}</div>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Title</label>
                    <input
                      type="text"
                      value={selectedContact.Title}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, Title: e.target.value }))
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Department</label>
                    <input
                      type="text"
                      value={selectedContact.Department}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, Department: e.target.value }))
                      }
                      disabled={isLoading}
                      placeholder="Department"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full p-2 border rounded-md ${formErrors.Type ? 'border-red-500' : ''}`}
                      value={selectedContact.Type}
                      onChange={(e) =>
                        setSelectedContact(prev => ({ ...prev, Type: e.target.value }))
                      }
                      disabled={isLoading}
                    >
                      <option value="">Choose</option>
                      {typeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {formErrors.Type && <div className="text-xs text-red-600 mt-1">{formErrors.Type}</div>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Region <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full p-2 border rounded-md ${formErrors.GMB_Region__c ? 'border-red-500' : ''}`}
                      value={selectedContact.GMB_Region__c}
                      onChange={(e) =>
                        setSelectedContact(prev => ({ ...prev, GMB_Region__c: e.target.value }))
                      }
                      disabled={isLoading}
                    >
                      <option value="">Choose</option>
                      {selectedContact.GMB_Region__c &&
                        !regionOptions.includes(selectedContact.GMB_Region__c) && (
                          <option value={selectedContact.GMB_Region__c}>
                            {selectedContact.GMB_Region__c}
                          </option>
                        )}
                      {regionOptions.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                    {formErrors.GMB_Region__c && <div className="text-xs text-red-600 mt-1">{formErrors.GMB_Region__c}</div>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone</label>
                    <input
                      type="text"
                      value={formatPhoneNumber(selectedContact.Phone)}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, Phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Mobile</label>
                    <input
                      type="text"
                      value={formatPhoneNumber(selectedContact.MobilePhone)}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, MobilePhone: e.target.value.replace(/\D/g, '').slice(0, 11) }))
                      }
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Fax</label>
                    <input
                      type="text"
                      value={selectedContact.Fax}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) =>
                        setSelectedContact((prev) => ({ ...prev, Fax: e.target.value }))
                      }
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="add-contact-form-grid add-contact-form-grid-2 mt-4">
                  <div>
                    <h3 className="block text-sm text-gray-600 mb-1">Mailing Address</h3>
                    <StreetAutocompleteInput
                      value={selectedContact.MailingAddress?.street || ''}
                      onChange={(text) => setSelectedContact((prev) => ({
                        ...prev,
                        MailingAddress: { ...prev.MailingAddress, street: text },
                      }))}
                      onSelectSuggestion={(s) => setSelectedContact((prev) => ({
                        ...prev,
                        MailingAddress: { ...prev.MailingAddress, street: s.description || s.main_text || '' },
                      }))}
                      onPlaceDetails={(d) => setSelectedContact((prev) => ({
                        ...prev,
                        MailingAddress: {
                          ...prev.MailingAddress,
                          street: [d.street_number, d.route].filter(Boolean).join(' ') || prev.MailingAddress.street,
                          city: d.city || prev.MailingAddress.city,
                          state: d.country_code === 'US' ? (toUSStateCode(d.state) || prev.MailingAddress.state) : prev.MailingAddress.state,
                          postalCode: d.postal_code || prev.MailingAddress.postalCode,
                          country: d.country_code || prev.MailingAddress.country,
                        }
                      }))}
                      placeholder="Street"
                      disabled={isLoading}
                    />
                    <div className="add-contact-address-fields">
                      <input
                        type="text"
                        value={selectedContact.MailingAddress?.city || ''}
                        placeholder="City"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            MailingAddress: { ...prev.MailingAddress, city: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={selectedContact.MailingAddress?.state || ''}
                        placeholder="State"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            MailingAddress: { ...prev.MailingAddress, state: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={selectedContact.MailingAddress?.postalCode || ''}
                        placeholder="Zip/Postal Code"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            MailingAddress: { ...prev.MailingAddress, postalCode: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={displayCountry(selectedContact.MailingAddress?.country)}
                        placeholder="Country"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            MailingAddress: { ...prev.MailingAddress, country: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="block text-sm text-gray-600 mb-1">Alternate Address</h3>
                    <StreetAutocompleteInput
                      value={selectedContact.OtherAddress?.street || ''}
                      onChange={(text) => setSelectedContact((prev) => ({
                        ...prev,
                        OtherAddress: { ...prev.OtherAddress, street: text },
                      }))}
                      onSelectSuggestion={(s) => setSelectedContact((prev) => ({
                        ...prev,
                        OtherAddress: { ...prev.OtherAddress, street: s.description || s.main_text || '' },
                      }))}
                      onPlaceDetails={(d) => setSelectedContact((prev) => ({
                        ...prev,
                        OtherAddress: {
                          ...prev.OtherAddress,
                          street: [d.street_number, d.route].filter(Boolean).join(' ') || prev.OtherAddress.street,
                          city: d.city || prev.OtherAddress.city,
                          state: d.country_code === 'US' ? (toUSStateCode(d.state) || prev.OtherAddress.state) : prev.OtherAddress.state,
                          postalCode: d.postal_code || prev.OtherAddress.postalCode,
                          country: d.country_code || prev.OtherAddress.country,
                        }
                      }))}
                      placeholder="Street"
                      disabled={isLoading}
                    />
                    <div className="add-contact-address-fields">
                      <input
                        type="text"
                        value={selectedContact.OtherAddress?.city || ''}
                        placeholder="City"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            OtherAddress: { ...prev.OtherAddress, city: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={selectedContact.OtherAddress?.state || ''}
                        placeholder="State"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            OtherAddress: { ...prev.OtherAddress, state: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={selectedContact.OtherAddress?.postalCode || ''}
                        placeholder="Zip/Postal Code"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            OtherAddress: { ...prev.OtherAddress, postalCode: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                      <input
                        type="text"
                        value={displayCountry(selectedContact.OtherAddress?.country)}
                        placeholder="Country"
                        className="p-2 border rounded-md"
                        onChange={(e) =>
                          setSelectedContact((prev) => ({
                            ...prev,
                            OtherAddress: { ...prev.OtherAddress, country: e.target.value },
                          }))
                        }
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-span-3">
                  <label className="block text-sm text-gray-600 mb-1">Account Billing State (Short Code)</label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={selectedContact.GMB_Account_Billing_State_Short_Code__c}
                    onChange={(e) =>
                      setSelectedContact(prev => ({
                        ...prev,
                        GMB_Account_Billing_State_Short_Code__c: e.target.value
                      }))
                    }
                    disabled={isLoading}
                  >
                    <option value="">Select the state</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="add-contact-modal-footer">
          {isSuccess ? (
            <button
              type="button"
              onClick={handleClose}
              className="add-contact-modal-submit"
            >
              Done
            </button>
          ) : mode === 'add' ? (
            <button
              type="button"
              className="add-contact-modal-submit"
              onClick={handleCreateContact}
              disabled={isLoading}
            >
              Create Contact
            </button>
          ) : (
            <button
              type="button"
              className="add-contact-modal-submit"
              onClick={handleUpdateContact}
              disabled={isLoading}
            >
              Update Contact
            </button>
          )}
        </div>
      </div>
      {showAddAccountModal && (
        <AddAccountModal
          isOpen={showAddAccountModal}
          onClose={() => setShowAddAccountModal(false)}
          onSuccess={() => {
            setShowAddAccountModal(false);
            setNoAccountFound(false);
          }}
        />
      )}
    </div>,
    document.body
  );
}
